<script setup>
/**
 * @description 信息面板弹窗：用于转账信息、优惠信息等大块只读内容展示，不承载二次确认语义
 */
import { Modal as AModal } from '@arco-design/web-vue'
import { IconClose } from '@arco-design/web-vue/es/icon'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { lockBodyScroll, unlockBodyScroll } from '~/utils/modal'
import BaseButton from '~/components/base/button/Button.vue'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: '提示',
  },
  okText: {
    type: String,
    default: '关闭',
  },
  cancelText: {
    type: String,
    default: '取消',
  },
  showCancel: {
    type: Boolean,
    default: false,
  },
  showOk: {
    type: Boolean,
    default: true,
  },
  okType: {
    type: String,
    default: 'default',
  },
  width: {
    type: [Number, String],
    default: 600,
  },
  okLoading: {
    type: Boolean,
    default: false,
  },
  okDisabled: {
    type: Boolean,
    default: false,
  },
  cancelDisabled: {
    type: Boolean,
    default: false,
  },
  beforeOk: {
    type: Function,
    default: null,
  },
  maskClosable: {
    type: Boolean,
    default: true,
  },
  closeDisabled: {
    type: Boolean,
    default: false,
  },
  panelClass: {
    type: [String, Array, Object],
    default: '',
  },
})

const emit = defineEmits(['update:visible', 'close', 'ok', 'cancel'])

const { isMobile } = useMediaQuery()
const hasEmittedClose = ref(false)
let hasBodyScrollLock = false

const modalWidth = computed(() => (isMobile.value ? '80%' : props.width))
const modalStyle = computed(() => ({
  borderRadius: isMobile.value ? '8px' : '12px',
  overflow: 'hidden',
}))

const emitCloseOnce = () => {
  if (hasEmittedClose.value) return
  hasEmittedClose.value = true
  emit('close')
}

const close = () => {
  if (props.closeDisabled) return
  emitCloseOnce()
  emit('update:visible', false)
}

const handleCancel = () => {
  if (props.cancelDisabled || props.okLoading) return
  emit('cancel')
  close()
}

const handleOk = async () => {
  if (props.okLoading || props.okDisabled) return
  if (props.beforeOk) {
    const result = await props.beforeOk()
    if (result === false) return
  }
  emit('ok')
  close()
}

const lockModalScroll = () => {
  if (hasBodyScrollLock) return
  lockBodyScroll({ cleanupBodyWidthCompensation: true })
  hasBodyScrollLock = true
}

const unlockModalScroll = () => {
  if (!hasBodyScrollLock) return
  unlockBodyScroll({ waitForBodyUnlock: true })
  hasBodyScrollLock = false
}

onMounted(() => {
  if (props.visible) {
    lockModalScroll()
  }
})

onBeforeUnmount(() => {
  unlockModalScroll()
})

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      hasEmittedClose.value = false
      lockModalScroll()
    } else {
      emitCloseOnce()
      unlockModalScroll()
    }
  }
)
</script>

<template>
  <component
    :is="AModal"
    :visible="visible"
    :footer="false"
    :closable="false"
    :mask-closable="maskClosable"
    :animation="false"
    :align-center="true"
    :width="modalWidth"
    :hide-title="true"
    :header="false"
    :modal-style="modalStyle"
    class="base-info-panel-modal-wrapper"
    @cancel="close"
  >
    <section :class="['base-info-panel-modal', panelClass]">
      <button
        type="button"
        class="base-info-panel-modal__close"
        :class="{ 'base-info-panel-modal__close--disabled': closeDisabled }"
        :disabled="closeDisabled"
        @click="close"
      >
        <IconClose />
      </button>
      <header class="base-info-panel-modal__header">
        <h2 class="base-info-panel-modal__title">{{ title }}</h2>
      </header>

      <div class="base-info-panel-modal__body">
        <slot />
      </div>

      <footer v-if="showCancel || showOk" class="base-info-panel-modal__footer">
        <BaseButton
          v-if="showCancel"
          type="default"
          class="base-info-panel-modal__btn"
          :disabled="cancelDisabled || okLoading"
          @click="handleCancel"
        >
          {{ cancelText }}
        </BaseButton>
        <BaseButton
          v-if="showOk"
          :type="okType"
          class="base-info-panel-modal__btn"
          :loading="okLoading"
          :disabled="okDisabled"
          @click="handleOk"
        >
          {{ okText }}
        </BaseButton>
      </footer>
    </section>
  </component>
</template>

<style scoped lang="scss">
.base-info-panel-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: calc(100dvh - 64px);
  min-height: 0;

  &__header {
    display: flex;
    flex-shrink: 0;
    align-items: flex-start;
    padding: 32px 72px 0 32px;

    @include respond-to-max('lg') {
      padding: 24px 56px 0 24px;
    }
  }

  &__title {
    flex: 1;
    min-width: 0;
    margin: 0;
    color: $text-title-color;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;

    @include respond-to-max('lg') {
      font-size: 18px;
    }
  }

  &__close {
    position: absolute;
    top: 32px;
    right: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    font-size: 24px;
    padding: 0;
    border: none;
    background: transparent;
    color: $text-subtitle-color;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: $text-title-color;
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 0 32px;
  }

  &__footer {
    display: flex;
    flex-shrink: 0;
    justify-content: flex-end;
    gap: 12px;
    padding: 0 32px 32px;

    @include respond-to-max('lg') {
      padding: 0 24px 24px;
    }
  }

  &__btn {
    height: 40px;
    min-width: 76px;
    padding: 0 16px;
    border-radius: 6px;
    font-size: 14px;
  }
}
</style>

<style lang="scss">
.base-info-panel-modal-wrapper {
  .arco-modal,
  .arco-modal-body {
    max-height: calc(100dvh - 64px);
    overflow: hidden;
  }

  .arco-modal-body {
    padding: 0;
    background-color: $bg-white;
  }
}
</style>
